// src/layout/AppLayout.js
import React from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Outlet, NavLink } from "react-router-dom";
import { Button } from "../components/ui/button";
import RoleSidebar from "../components/RoleSidebar";
import MobileNav from "../components/MobileNav";
import { getMenuItems } from "../utils/menuItems";

function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Sidebar - Hidden on small screens, visible from 1024px up */}
            <div className="hidden lg:block">
                <RoleSidebar />
            </div>

            {/* Mobile Navigation - Visible only below 626px */}
            <div className="block sm:hidden">
                <MobileNav />
            </div>

            {/* Small Tablet Navigation - Visible between 626px and 764px */}
            <div className="hidden sm:block md:hidden">
                <nav className="text-white bg-gray-900 shadow-lg">
                    <div className="container px-4 mx-auto">
                        <div className="flex items-center h-14">
                            <div className="flex items-center justify-between flex-1">
                                <div className="flex-shrink-0">
                                    <span className="text-lg font-bold">
                                        STOJALITICS
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    {getMenuItems(
                                        user?.role,
                                        user?.permissions
                                    ).map((item, index) => (
                                        <NavLink
                                            key={index}
                                            to={item.path}
                                            className={({ isActive }) =>
                                                `p-2 rounded-md transition-colors flex items-center ${
                                                    isActive
                                                        ? "bg-primary text-white"
                                                        : "text-gray-300 hover:bg-gray-700 hover:text-white"
                                                }`
                                            }
                                            title={item.label}
                                        >
                                            <span className="text-lg">
                                                {item.icon}
                                            </span>
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            </div>

            {/* Tablet Navigation - Visible between 764px and 1024px */}
            <div className="fixed top-0 left-0 z-10 hidden w-16 h-full text-white bg-gray-900 shadow-lg md:block lg:hidden">
                <div className="flex flex-col items-center py-4 space-y-4">
                    <div className="mb-4">
                        <span className="block text-xl font-bold origin-left transform rotate-90 translate-x-4 whitespace-nowrap">
                            STOJALITICS
                        </span>
                    </div>
                    {getMenuItems(user?.role, user?.permissions).map(
                        (item, index) => (
                            <NavLink
                                key={index}
                                to={item.path}
                                className={({ isActive }) =>
                                    `p-3 rounded-lg transition-colors flex items-center justify-center ${
                                        isActive
                                            ? "bg-primary text-white"
                                            : "text-gray-300 hover:bg-gray-700 hover:text-white"
                                    }`
                                }
                                title={item.label}
                            >
                                <span className="text-xl">{item.icon}</span>
                            </NavLink>
                        )
                    )}
                </div>
            </div>

            {/* Main content - Adjusted padding for all screen sizes */}
            <div className="flex flex-col flex-1 lg:pl-56 md:pl-16">
                {/* Top navigation - just for user info and logout */}
                <nav className="bg-white shadow-sm">
                    <div className="flex items-center justify-end h-16 px-4 mx-auto max-w-7xl">
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-700">
                                {user?.name || user?.email}
                            </span>
                            <Button variant="outline" onClick={handleLogout}>
                                Odjava
                            </Button>
                        </div>
                    </div>
                </nav>

                {/* Page content */}
                <main className="flex-1 p-4">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

export default AppLayout;
